/**
 * Policy draft review page (M19.7) at /ai/ingestion/drafts/{id}.
 *
 * Lists every extracted item grouped by domain. HR can approve /
 * reject / edit each item, then bulk-promote per domain (writes to
 * the live config tables).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Sparkles, Upload, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  EmptyState,
  EntityDrawer,
  PageHeader,
  WorkflowStateBadge,
} from "../components/ui";
import { api, ApiError } from "../lib/api";
import type {
  PolicyExtractedItem,
  PolicyExtractionDraftFull,
} from "../lib/api";

function confidencePill(value: string | null): {
  bg: string;
  text: string;
  label: string;
} {
  if (value === null) return {
    bg: "bg-surface-muted",
    text: "text-text-secondary",
    label: "—",
  };
  const num = parseFloat(value);
  if (num >= 0.85)
    return {
      bg: "bg-emerald-100",
      text: "text-emerald-700",
      label: `${(num * 100).toFixed(0)}%`,
    };
  if (num >= 0.5)
    return {
      bg: "bg-amber-100",
      text: "text-amber-700",
      label: `${(num * 100).toFixed(0)}%`,
    };
  return {
    bg: "bg-rose-100",
    text: "text-rose-700",
    label: `${(num * 100).toFixed(0)}%`,
  };
}

function RejectDialog({
  draftId,
  itemId,
  onClose,
  onDone,
}: {
  draftId: string;
  itemId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () =>
      api.rejectPolicyItem(draftId, itemId, {
        actor: "hr",
        reason,
      }),
    onSuccess: () => onDone(),
    onError: (err) =>
      setErrorMsg(err instanceof ApiError ? err.detail : String(err)),
  });
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("policy.draft_reject_title")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form="reject-policy-form"
            disabled={m.isPending || !reason.trim()}
            className="rounded bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {m.isPending
              ? t("policy.draft_rejecting")
              : t("policy.draft_confirm_reject")}
          </button>
        </div>
      }
    >
      <form
        id="reject-policy-form"
        onSubmit={(e) => {
          e.preventDefault();
          setErrorMsg(null);
          m.mutate();
        }}
        className="flex flex-col gap-3 text-sm"
      >
        <label>
          <span className="text-xs font-medium uppercase text-text-secondary">
            {t("policy.draft_reject_reason")}
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            required
            className="mt-1 w-full rounded border border-border-subtle bg-surface-default px-3 py-2"
          />
        </label>
        {errorMsg && (
          <p className="rounded bg-rose-50 p-2 text-rose-700">
            {errorMsg}
          </p>
        )}
      </form>
    </EntityDrawer>
  );
}

function LoanProductPreview({ payload }: { payload: Record<string, unknown> }) {
  // M3X.LOANS-specific card renderer for loan_products domain.
  // Shows the load-bearing knobs (identity, amount/tenure caps, sharia,
  // eligibility) inline, and a collapsible JSON fallback for the rest.
  const fields: Array<[string, string]> = [
    ["code", String(payload.code ?? "—")],
    ["name_en", String(payload.name_en ?? "—")],
    ["name_ar", String(payload.name_ar ?? "—")],
    ["max_amount", String(payload.max_amount ?? "—")],
    ["max_tenure_months", String(payload.max_tenure_months ?? "—")],
    ["interest_rate", String(payload.interest_rate ?? "0")],
    ["sharia_structure_type", String(payload.sharia_structure_type ?? "none")],
    [
      "min_service_months_eligibility",
      String(payload.min_service_months_eligibility ?? "0"),
    ],
    [
      "max_concurrent_loans_per_employee",
      String(payload.max_concurrent_loans_per_employee ?? "1"),
    ],
  ];
  return (
    <div className="mt-2 space-y-2">
      <div className="grid grid-cols-2 gap-2 rounded bg-surface-muted p-2">
        {fields.map(([k, v]) => (
          <div key={k} className="flex flex-col">
            <span className="text-[10px] uppercase text-text-tertiary">
              {k}
            </span>
            <span className="font-medium">{v}</span>
          </div>
        ))}
      </div>
      <details className="rounded bg-surface-muted p-2">
        <summary className="cursor-pointer text-[10px] text-text-tertiary">
          Full JSON
        </summary>
        <pre className="mt-2 overflow-x-auto font-mono text-[10px]">
          {JSON.stringify(payload, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function LeaveTypePreview({ payload }: { payload: Record<string, unknown> }) {
  const fields: Array<[string, string]> = [
    ["code", String(payload.code ?? "—")],
    ["name_en", String(payload.name_en ?? "—")],
    ["name_ar", String(payload.name_ar ?? "—")],
    ["category", String(payload.category ?? "—")],
    ["number_of_days", String(payload.number_of_days ?? "—")],
    ["paid", String(payload.paid ?? payload.pay_leave_with_salary ?? "—")],
    [
      "requires_attachment",
      String(payload.requires_attachment ?? "—"),
    ],
    [
      "encashable_on_eos",
      String(payload.encashable_on_eos ?? "—"),
    ],
  ];
  return (
    <div className="mt-2 space-y-2">
      <div className="grid grid-cols-2 gap-2 rounded bg-surface-muted p-2">
        {fields.map(([k, v]) => (
          <div key={k} className="flex flex-col">
            <span className="text-[10px] uppercase text-text-tertiary">
              {k}
            </span>
            <span className="font-medium">{v}</span>
          </div>
        ))}
      </div>
      <details className="rounded bg-surface-muted p-2">
        <summary className="cursor-pointer text-[10px] text-text-tertiary">
          Full JSON
        </summary>
        <pre className="mt-2 overflow-x-auto font-mono text-[10px]">
          {JSON.stringify(payload, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function ItemCard({
  draftId,
  item,
  onChanged,
}: {
  draftId: string;
  item: PolicyExtractedItem;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [rejecting, setRejecting] = useState(false);
  const approve = useMutation({
    mutationFn: () =>
      api.approvePolicyItem(draftId, item.id, { actor: "hr" }),
    onSuccess: onChanged,
  });
  const conf = confidencePill(item.overall_confidence);
  const isPending = item.approval_status === "pending";
  return (
    <article className="rounded-lg border border-border-subtle bg-surface-default p-3 text-xs">
      <header className="flex items-center gap-2">
        <span className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-text-secondary">
          #{item.sequence_index}
        </span>
        <span className={`rounded px-1.5 py-0.5 ${conf.bg} ${conf.text}`}>
          {conf.label}
        </span>
        {item.source_pages.length > 0 && (
          <span className="text-text-secondary">
            p.{item.source_pages.join(", ")}
          </span>
        )}
        <span className="ms-auto">
          <WorkflowStateBadge state={item.approval_status} />
        </span>
        {isPending && (
          <>
            <button
              type="button"
              onClick={() => setRejecting(true)}
              className="flex items-center gap-1 rounded border border-rose-300 px-2 py-0.5 text-rose-700 hover:bg-rose-50"
            >
              <X size={12} /> {t("policy.draft_reject")}
            </button>
            <button
              type="button"
              onClick={() => approve.mutate()}
              disabled={approve.isPending}
              className="flex items-center gap-1 rounded bg-emerald-700 px-2 py-0.5 font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <Check size={12} /> {t("policy.draft_approve")}
            </button>
          </>
        )}
      </header>
      {item.domain === "loan_products" ? (
        <LoanProductPreview payload={item.payload_json} />
      ) : item.domain === "leave_types" ? (
        <LeaveTypePreview payload={item.payload_json} />
      ) : (
        <pre className="mt-2 overflow-x-auto rounded bg-surface-muted p-2 font-mono text-[10px]">
          {JSON.stringify(item.payload_json, null, 2)}
        </pre>
      )}
      {item.rejection_reason && (
        <p className="mt-2 rounded bg-rose-50 p-2 text-rose-700">
          {item.rejection_reason}
        </p>
      )}
      {rejecting && (
        <RejectDialog
          draftId={draftId}
          itemId={item.id}
          onClose={() => setRejecting(false)}
          onDone={() => {
            setRejecting(false);
            onChanged();
          }}
        />
      )}
    </article>
  );
}

function DomainSection({
  draftId,
  domain,
  items,
  onChanged,
}: {
  draftId: string;
  domain: string;
  items: PolicyExtractedItem[];
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [promoteErr, setPromoteErr] = useState<string | null>(null);
  const promote = useMutation({
    mutationFn: () =>
      api.promotePolicyDraft(draftId, {
        actor: "hr",
        domains: [domain],
      }),
    onSuccess: (summary) => {
      setPromoteErr(null);
      if ((summary.failed ?? 0) > 0 && summary.errors?.length) {
        setPromoteErr(summary.errors.map((e) => e.message).join("; "));
      }
      qc.invalidateQueries({ queryKey: ["policy-promotion-status"] });
      qc.invalidateQueries({ queryKey: ["allowances"] });
      qc.invalidateQueries({ queryKey: ["grades"] });
      qc.invalidateQueries({ queryKey: ["leave-types"] });
      qc.invalidateQueries({ queryKey: ["loan-products"] });
      onChanged();
    },
    onError: (e) => {
      setPromoteErr(e instanceof Error ? e.message : String(e));
    },
  });
  const approvedCount = items.filter(
    (i) => i.approval_status === "approved" || i.approval_status === "edited",
  ).length;
  const pendingPromoteCount = items.filter(
    (i) =>
      (i.approval_status === "approved" || i.approval_status === "edited") &&
      !i.live_entity_id,
  ).length;
  const anyApproved = approvedCount > 0;
  const promoteLabel =
    pendingPromoteCount > 0
      ? t("policy.draft_promote_n_to_live", { n: pendingPromoteCount })
      : t("policy.draft_resync_to_live");
  return (
    <section className="flex flex-col gap-2 rounded-xl border border-border-subtle bg-surface-default p-4">
      <header className="flex items-center justify-between">
        <h3 className="text-sm font-semibold capitalize">
          {domain.replace("_", " ")}{" "}
          <span className="text-text-secondary">
            ({items.length})
          </span>
        </h3>
        <button
          type="button"
          onClick={() => promote.mutate()}
          disabled={!anyApproved || promote.isPending}
          className="flex items-center gap-1 rounded border border-border-default px-2 py-1 text-xs hover:bg-surface-muted disabled:opacity-50"
        >
          <Upload size={12} />
          {promote.isPending
            ? t("policy.draft_promoting")
            : promoteLabel}
        </button>
      </header>
      {promoteErr && (
        <p className="rounded bg-rose-50 px-2 py-1 text-xs text-rose-700">
          {promoteErr}
        </p>
      )}
      <div className="flex flex-col gap-2">
        {items.map((item) => (
          <ItemCard
            key={item.id}
            draftId={draftId}
            item={item}
            onChanged={onChanged}
          />
        ))}
      </div>
    </section>
  );
}

export function PolicyDraftPage() {
  const { t } = useTranslation();
  const { draftId } = useParams<{ draftId: string }>();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["policy-draft", draftId],
    queryFn: () => api.getPolicyDraft(draftId!),
    enabled: !!draftId,
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  if (!q.data || !draftId)
    return <ErrorPanel message={t("policy.draft_not_found")} />;

  const data: PolicyExtractionDraftFull = q.data;
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["policy-draft", draftId] });
  };
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("policy.draft_title")}
        subtitle={t("policy.draft_subtitle", {
          domains: data.draft.extracted_domains.length,
          pages: data.draft.page_count,
          conf: data.draft.extraction_confidence_overall ?? "—",
        })}
        breadcrumbs={[
          { label: t("nav.policies"), href: "/policies" },
          {
            label: t("nav.ai_ingestion"),
            href: "/policies/ai-ingestion",
          },
          { label: data.draft.id.slice(0, 8) },
        ]}
        actions={
          <span className="flex items-center gap-2">
            <Sparkles size={14} className="text-brand" />
            <WorkflowStateBadge state={data.draft.status} />
          </span>
        }
      />

      {Object.keys(data.items_by_domain).length === 0 ? (
        <EmptyState
          headline={t("policy.draft_no_items_headline")}
          description={t("policy.draft_no_items_body")}
        />
      ) : (
        Object.entries(data.items_by_domain).map(([domain, items]) => (
          <DomainSection
            key={domain}
            draftId={draftId}
            domain={domain}
            items={items}
            onChanged={refresh}
          />
        ))
      )}
    </div>
  );
}
